import { create } from 'zustand';
import { supabase } from '../supabase';
import { useAuthStore } from './useAuthStore';
import { useOfflineQueue } from './useOfflineQueue';
import { usePieceStore } from './usePieceStore';

interface CuttingState {
    loading: boolean;
    error: string | null;
    
    startCutting: (sampleId: string, startDate?: string) => Promise<void>;
    endCutting: (sampleId: string, endDate?: string, remarks?: string, leftoverData?: any) => Promise<void>;
}

export const useCuttingStore = create<CuttingState>((set, get) => ({
    loading: false,
    error: null,

    startCutting: async (sampleId, startDate) => {
        if (!navigator.onLine) {
            await useOfflineQueue.getState().addToQueue('startCutting', { sampleId, startDate });
            return;
        }
        const u = useAuthStore.getState().userProfile;
        if (!u?.factory_id) return;

        set({ loading: true, error: null });
        try {
            // Generate idempotency key for offline queue safety
            const idempotencyKey = `start_cutting_${sampleId}_${startDate || new Date().toISOString()}`;

            // Single RPC call - all logic in database
            const { data, error } = await supabase.rpc('rpc_start_cutting', {
                p_factory_id: u.factory_id,
                p_sample_request_id: sampleId,
                p_user_id: u.id,
                p_user_role: u.role,
                p_started_at: startDate || new Date().toISOString(),
                p_idempotency_key: idempotencyKey
            });

            if (error) throw error;
            
            if (!data.success) {
                throw new Error(data.error_message);
            }

            console.log(`✅ Cutting started: ${data.affected_count} pieces`);
            
            // Refetch UI data
            await Promise.all([
                usePieceStore.getState().fetchAssignedSamples(),
                usePieceStore.getState().fetchRecentLogs()
            ]);

        } catch (e: any) {
            console.error('❌ startCutting:', e);
            set({ error: e.message });
        } finally {
            set({ loading: false });
        }
    },

    endCutting: async (sampleId, endDate, remarks, leftoverData) => {
        if (!navigator.onLine) {
            await useOfflineQueue.getState().addToQueue('endCutting', { sampleId, endDate, remarks, leftoverData });
            return;
        }
        const u = useAuthStore.getState().userProfile;
        if (!u?.factory_id) return;

        set({ loading: true, error: null });
        try {
            const idempotencyKey = `end_cutting_${sampleId}_${endDate || new Date().toISOString()}`;

            const { data, error } = await supabase.rpc('rpc_end_cutting', {
                p_factory_id: u.factory_id,
                p_sample_request_id: sampleId,
                p_user_id: u.id,
                p_user_role: u.role,
                p_ended_at: endDate || new Date().toISOString(),
                p_fabric_leftover: leftoverData || [],
                p_idempotency_key: idempotencyKey
            });

            if (error) throw error;
            
            if (!data.success) {
                throw new Error(data.error_message);
            }

            console.log(`✅ Cutting completed: ${data.affected_count} pieces`);
            
            await Promise.all([
                usePieceStore.getState().fetchAssignedSamples(),
                usePieceStore.getState().fetchRecentLogs()
            ]);

        } catch (e: any) {
            console.error('❌ endCutting:', e);
            set({ error: e.message });
        } finally {
            set({ loading: false });
        }
    },
}));
